import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import {
    LuCheckCheck,
    LuChevronLeft,
    LuSparkles,
    LuUser,
    LuBookOpen,
    LuWrench,
    LuSettings2,
    LuUpload,
    LuTrash2,
    LuFileText,
    LuSend,
    LuRotateCcw,
    LuHeadphones,
    LuCalendarClock,
    LuShoppingCart,
    LuKanban,
    LuListPlus,
    LuCreditCard,
    LuLayoutGrid,
    LuInfo,
    LuTag,
    LuSave,
    LuPlus,
    LuMic,
    LuSquare,
    LuPaperclip,
} from "react-icons/lu";
import useAPI from "../../hooks/useAPI";
import { upload_s3 } from "./UploaderS3";
import { sendToast, questionToast } from "../plugins/toastAll";
import { BOT_LIST_CSS, useFullBleed } from "./BotsContent/Others/BotListKit";
import { WA_CSS, formatWhatsApp, nowTime } from "./BotsContent/Agent/AgentTester";
import { SCROLL_CLASS, SCROLL_CSS, isDarkMode, useFillHeight } from "./BotsContent/Others/BotCard";

const AS_THEMES = {
    dark: { dialog: "#111b21", header: "#111b21", title: "#e9edef", text: "#e9edef", soft: "#aebac1", muted: "#9fb4b9", optionBg: "#111b21", optionBorder: "#2a3942", selectedBg: "#202c33", inputBg: "#111b21", inputBorder: "#2a3942", aside: "#0c1317", asideBorder: "#2a3942", chip: "#202c33", chipActive: "#0a7ba5", navOn: "#153543", navOnText: "#5DCCED", danger: "#ff7a8c" },
    light: { dialog: "#ffffff", header: "#ffffff", title: "#2D2D2D", text: "#2D2D2D", soft: "#555555", muted: "#747474", optionBg: "#ffffff", optionBorder: "#e3e9ec", selectedBg: "#ffffff", inputBg: "#ffffff", inputBorder: "#e3e9ec", aside: "#e6f4f9", asideBorder: "#e3e9ec", chip: "#f1f6f8", chipActive: "#0a7ba5", navOn: "#dff3fa", navOnText: "#0a7ba5", danger: "#d6102f" },
};

const DEFAULTS = {
    role: "Eres un asistente de atención al cliente muy útil, importante y profesional.",
    instructions_message:
        "Tu objetivo es proporcionar respuestas claras y precisas, resolver problemas de manera efectiva y asegurarte de que los clientes se sientan valorados y satisfechos con la atención recibida. Siempre actúas con empatía, cortesía y eficiencia para mejorar la experiencia del cliente.",
    messageTransfer: "Espera un momento, ya te atiende uno de nuestros agentes.",
};

const LANGUAGES = [
    { value: "1", label: "Español" },
    { value: "2", label: "Inglés" },
];

const MODELS = [
    { value: "gpt-4o-mini", label: "gpt-4o-mini (actual)" },
    { value: "gpt-4.1-mini", label: "gpt-4.1-mini" },
    { value: "gpt-4.1", label: "gpt-4.1" },
];

const REMINDER_HOURS = [
    { value: "3600000", label: "1 hora antes" },
    { value: "7200000", label: "2 horas antes" },
    { value: "21600000", label: "6 horas antes" },
    { value: "43200000", label: "12 horas antes" },
    { value: "86400000", label: "24 horas antes" },
];

const AUDIO_LIMITS = [
    { value: "30", label: "30 segundos" },
    { value: "60", label: "1 minuto" },
    { value: "120", label: "2 minutos" },
    { value: "300", label: "5 minutos" },
];

const MAX_TEST_AUDIO_BYTES = 3 * 1024 * 1024;

const FILE_TYPES = [
    "application/pdf",
    "text/plain",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];

const CONTACT_FIELDS = [
    { name: "name", description: "Nombre del contacto", label: "Nombre" },
    { name: "lastName", description: "Apellido del contacto", label: "Apellido" },
    { name: "email", description: "Correo del contacto", label: "Correo" },
];

const CRM_TOOLS = [
    {
        key: "stage",
        itemsKey: "stages",
        optionLabel: "name",
        title: "Mover a etapa del embudo",
        text: "Actualiza el CRM de SendingUp según el avance del contacto.",
        icon: LuKanban,
        color: "#00a884",
        pick: "Etapas a las que puede mover",
        when: "Cuando el contacto muestre interés real o avance en la compra",
        empty: "No hay etapas creadas en el CRM.",
    },
    {
        key: "list",
        itemsKey: "lists",
        optionLabel: "name",
        title: "Agregar a lista",
        text: "Suma al contacto a una lista para campañas y seguimientos.",
        icon: LuListPlus,
        color: "#00bcd4",
        pick: "Listas permitidas",
        when: "Cuando el contacto acepte recibir novedades",
        empty: "No hay listas creadas.",
    },
    {
        key: "tags",
        itemsKey: "tags",
        optionLabel: "title",
        title: "Etiquetar",
        text: "Marca el motivo o el interés de la conversación.",
        icon: LuTag,
        color: "#5bc0de",
        pick: "Etiquetas que puede poner",
        when: "Cuando identifiques el motivo de la conversación",
        empty: "No hay etiquetas creadas.",
    },
    {
        key: "fields",
        itemsKey: "fields",
        optionLabel: "label",
        title: "Guardar dato del contacto",
        text: "Guarda en la ficha del contacto lo que te diga: correo, presupuesto, ciudad…",
        icon: LuSave,
        color: "#d89c7e",
        pick: "Campos que puede llenar",
        when: "En cuanto el contacto te dé uno de esos datos",
        empty: "No hay campos disponibles.",
    },
];

const buildCrm = (crmTools = {}) => ({
    ...CRM_TOOLS.reduce((acc, tool) => {
        const saved = crmTools[tool.key] || {};
        acc[tool.key] = { enabled: Boolean(saved.enabled), whenToUse: saved.whenToUse || "", [tool.itemsKey]: Array.isArray(saved[tool.itemsKey]) ? saved[tool.itemsKey] : [] };
        return acc;
    }, {}),
    payment: {
        enabled: Boolean(crmTools.payment?.enabled),
        platform: crmTools.payment?.platform || "",
        amountMode: crmTools.payment?.amountMode || "fixed",
        amount: crmTools.payment?.amount || "",
        maxAmount: crmTools.payment?.maxAmount || "",
        title: crmTools.payment?.title || "",
        whenToUse: crmTools.payment?.whenToUse || "",
    },
    catalog: {
        enabled: Boolean(crmTools.catalog?.enabled),
        message: crmTools.catalog?.message || "Mira nuestro catálogo 👇",
        whenToUse: crmTools.catalog?.whenToUse || "",
    },
});

const PLATFORMS = [
    { value: "Epayco", label: "ePayco" },
    { value: "Wompi", label: "Wompi" },
];

const SECTIONS = [
    { key: "personality", label: "Personalidad", icon: LuUser, color: "#7563D8" },
    { key: "knowledge", label: "Conocimiento", icon: LuBookOpen, color: "#2fae4e" },
    { key: "tools", label: "Herramientas", icon: LuWrench, color: "#f26b1d" },
    { key: "settings", label: "Ajustes", icon: LuSettings2, color: "#8B8B8B" },
];

const templateParams = (template) => {
    const body = (template?.content || []).find((item) => item.type === "BODY");
    return (body?.text || "").match(/{{(.*?)}}/g) || [];
};

const buildDraft = (assistant) => {
    const transfer = assistant?.transfer || {};
    const schedule = assistant?.schedule || {};
    const sales = assistant?.sales || {};
    return {
        id: assistant?.id || "",
        name: assistant?.name || "",
        language: assistant?.language || "1",
        role: assistant?.id ? assistant.role || "" : DEFAULTS.role,
        instructions_message: assistant?.id ? assistant.instructions_message || "" : DEFAULTS.instructions_message,
        source: assistant?.url ? "2" : "1",
        content: assistant?.content || "",
        url: assistant?.url || "",
        model: assistant?.model || "gpt-4o-mini",
        files: Array.isArray(assistant?.files) ? assistant.files : [],
        transferOn: Boolean(transfer.isTransfer),
        transferType: transfer.type === "area" ? "area" : "agent",
        transferTarget: transfer.value ? { label: transfer.label, value: transfer.value, type: transfer.type } : null,
        transferMessage: transfer.message || DEFAULTS.messageTransfer,
        scheduleOn: Boolean(schedule.isSchedule),
        scheduleWebhook: schedule.webhook || "",
        scheduleSearch: schedule.webhookDataSearchEvents || "",
        scheduleCancel: schedule.webhookDataCancelEvent || "",
        reminderTemplate: schedule.templateSchedule || null,
        reminderTime: schedule.reminderTime || null,
        salesOn: Boolean(sales.activeSale),
        salesWebhook: sales.webhook || "",
        crm: buildCrm(assistant?.crmTools),
        faqs: Array.isArray(assistant?.faqs) ? assistant.faqs.map((faq) => ({ question: faq.question || "", answer: faq.answer || "" })) : [],
        audioOn: assistant?.audio?.transcribe !== false,
        audioMax: String(assistant?.audio?.maxSeconds || 120),
        active: assistant?.active !== false,
    };
};

const toPayload = (draft) => {
    const payload = {
        name: draft.name.trim(),
        language: draft.language,
        role: draft.role,
        instructions_message: draft.instructions_message,
        instructions: draft.source,
        content: draft.source === "1" ? draft.content : "",
        url: draft.source === "2" ? draft.url.trim() : "",
        model: draft.model,
        files: draft.files,
        transfer: draft.transferOn
            ? { ...(draft.transferTarget || {}), isTransfer: true, message: draft.transferMessage }
            : { isTransfer: false },
        schedule: draft.scheduleOn
            ? {
                  isSchedule: true,
                  webhook: draft.scheduleWebhook.trim(),
                  webhookDataSearchEvents: draft.scheduleSearch.trim(),
                  webhookDataCancelEvent: draft.scheduleCancel.trim(),
                  reminderTime: draft.reminderTime,
                  templateSchedule: draft.reminderTemplate,
              }
            : { isSchedule: false },
        sales: draft.salesOn ? { activeSale: true, webhook: draft.salesWebhook.trim() } : { activeSale: false },
        crmTools: draft.crm,
        faqs: draft.faqs
            .map((faq) => ({ question: faq.question.trim(), answer: faq.answer.trim() }))
            .filter((faq) => faq.question && faq.answer),
        audio: { transcribe: draft.audioOn, maxSeconds: Number(draft.audioMax) },
        active: draft.active,
    };
    if (draft.id) payload.id = draft.id;
    return payload;
};

const validate = (draft) => {
    if (!draft.name.trim()) return { section: "personality", message: "Ponle un nombre al asistente." };
    if (draft.source === "2" && !/docs\.google\.com\/spreadsheets/.test(draft.url)) {
        return { section: "knowledge", message: "Pega el enlace de una hoja de Google Sheets." };
    }
    if (draft.faqs.some((faq) => Boolean(faq.question.trim()) !== Boolean(faq.answer.trim()))) {
        return { section: "knowledge", message: "Cada pregunta frecuente necesita su pregunta y su respuesta." };
    }
    if (draft.transferOn && !draft.transferTarget) {
        return { section: "tools", message: "Elige a qué asesor o área se transfiere." };
    }
    if (draft.scheduleOn) {
        if (!draft.scheduleWebhook.trim()) return { section: "tools", message: "Agrega el webhook para agendar citas." };
        if (!draft.reminderTemplate) return { section: "tools", message: "Elige la plantilla del recordatorio." };
        if (templateParams(draft.reminderTemplate).length !== 4) {
            return { section: "tools", message: "La plantilla del recordatorio debe tener 4 parámetros." };
        }
        if (!draft.reminderTime) return { section: "tools", message: "Elige cuándo se envía el recordatorio." };
    }
    if (draft.salesOn && !draft.salesWebhook.trim()) return { section: "tools", message: "Agrega el webhook para registrar ventas." };
    const emptyCrm = CRM_TOOLS.find((tool) => draft.crm[tool.key]?.enabled && !draft.crm[tool.key][tool.itemsKey].length);
    if (emptyCrm) return { section: "tools", message: `En «${emptyCrm.title}» elige al menos una opción.` };
    const payment = draft.crm.payment;
    if (payment.enabled) {
        if (!payment.platform) return { section: "tools", message: "Elige la pasarela del link de pago." };
        if (payment.amountMode === "ai" && !(Number(payment.maxAmount) > 0)) return { section: "tools", message: "Define el monto máximo que puede cobrar la IA." };
        if (payment.amountMode !== "ai" && !(Number(payment.amount) > 0)) return { section: "tools", message: "Define el monto del link de pago." };
    }
    return null;
};

const selectStyles = (t) => ({
    control: (provided) => ({
        ...provided,
        backgroundColor: t.inputBg,
        borderColor: t.inputBorder,
        borderRadius: "10px",
        minHeight: "40px",
        boxShadow: "none",
        "&:hover": { borderColor: t.inputBorder },
    }),
    menu: (provided) => ({ ...provided, backgroundColor: t.dialog, border: `1px solid ${t.optionBorder}`, borderRadius: "12px", overflow: "hidden" }),
    menuList: (provided) => ({ ...provided, padding: "6px" }),
    menuPortal: (provided) => ({ ...provided, zIndex: 1400 }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? t.navOn : state.isFocused ? t.chip : "transparent",
        color: state.isSelected ? t.navOnText : t.text,
        cursor: "pointer",
        fontSize: "13px",
    }),
    singleValue: (provided) => ({ ...provided, color: t.text, fontSize: "14px" }),
    input: (provided) => ({ ...provided, color: t.text }),
    placeholder: (provided) => ({ ...provided, color: t.muted, fontSize: "14px" }),
    indicatorSeparator: () => ({ display: "none" }),
});

const Toggle = ({ id, checked, onChange }) => (
    <button
        type="button"
        id={id}
        role="switch"
        aria-checked={Boolean(checked)}
        className={`su-sw${checked ? " on" : ""}`}
        onClick={(event) => {
            event.stopPropagation();
            onChange(!checked);
        }}
    >
        <span />
    </button>
);

const AssistantEditor = ({ assistant, isDark, listAreas = [], listAdvisers = [], templates = [], crmOptions = {}, onBack, onSaved }) => {
    const { urlAPI_1, urlAPI_3, fetchWithAuth } = useAPI();
    const theme = isDarkMode(isDark) ? AS_THEMES.dark : AS_THEMES.light;
    const styles = useMemo(() => selectStyles(theme), [theme]);
    const [rootRef, rootHeight] = useFillHeight(0);
    useFullBleed(rootRef);
    const initial = useMemo(() => buildDraft(assistant), [assistant]);
    const [draft, setDraft] = useState(initial);
    const [section, setSection] = useState("personality");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [chat, setChat] = useState([]);
    const [chatText, setChatText] = useState("");
    const [chatBusy, setChatBusy] = useState(false);
    const [previousResponseId, setPreviousResponseId] = useState(null);
    const [recording, setRecording] = useState(false);
    const fileInputRef = useRef(null);
    const chatEndRef = useRef(null);
    const recorderRef = useRef(null);
    const audioInputRef = useRef(null);

    const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
    const set = (patch) => {
        setDraft((prev) => ({ ...prev, ...patch }));
        setError(null);
    };

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ block: "end" });
    }, [chat, chatBusy]);

    const reminderTemplates = useMemo(() => templates.filter((template) => templateParams(template).length === 4), [templates]);
    const activeTools = [draft.transferOn, draft.scheduleOn, draft.salesOn, draft.crm.payment.enabled, draft.crm.catalog.enabled, ...CRM_TOOLS.map((tool) => draft.crm[tool.key]?.enabled)].filter(Boolean).length;
    const crmChoices = {
        stages: crmOptions.stages || [],
        lists: crmOptions.lists || [],
        tags: crmOptions.tags || [],
        fields: [
            ...CONTACT_FIELDS,
            ...(crmOptions.fields || []).filter((field) => !CONTACT_FIELDS.some((base) => base.name === field.name)).map((field) => ({ name: field.name, label: field.name })),
        ],
    };
    const setCrm = (key, patch) => set({ crm: { ...draft.crm, [key]: { ...draft.crm[key], ...patch } } });
    const knowledgeCount = draft.files.length + (draft.source === "2" && draft.url ? 1 : 0) + draft.faqs.filter((faq) => faq.question.trim() && faq.answer.trim()).length;
    const setFaq = (index, patch) => set({ faqs: draft.faqs.map((faq, i) => (i === index ? { ...faq, ...patch } : faq)) });

    const getSignedUrl = async (file) => {
        const response = await fetchWithAuth(`${urlAPI_1}getSignedURL/${encodeURIComponent(file.name)}/${file.size}`, { method: "GET", mode: "cors" });
        if (response.status !== 200) return null;
        const signed = await response.json();
        return signed?.error ? null : signed;
    };

    const onFiles = async (event) => {
        const picked = Array.from(event.target.files || []);
        event.target.value = "";
        if (!picked.length) return;
        setUploading(true);
        const added = [];
        try {
            for (const file of picked) {
                if (!FILE_TYPES.includes(file.type)) {
                    sendToast({ type: "info", message: `${file.name}: solo se aceptan PDF, TXT, Word o PowerPoint.` });
                    continue;
                }
                if (file.size > 100 * 1024 * 1024) {
                    sendToast({ type: "info", message: `${file.name} supera los 100 MB.` });
                    continue;
                }
                const signed = await getSignedUrl(file);
                const url = signed ? await upload_s3(file, signed) : null;
                if (!url) {
                    sendToast({ type: "error", message: `No se pudo subir ${file.name}.` });
                    continue;
                }
                added.push({ id: "", name: file.name, url });
            }
        } finally {
            setUploading(false);
        }
        if (added.length) set({ files: [...draft.files, ...added] });
    };

    const removeFile = async (file) => {
        if (file.id) {
            const result = await questionToast(
                {
                    isFetch: true,
                    fetch: { url: `${urlAPI_3}file/${file.id}`, options: { method: "DELETE", mode: "cors" } },
                    title: `¿Eliminar ${file.name}?`,
                    text: "El asistente dejará de usar este archivo.",
                    icon: "warning",
                    buttons: { success: "Eliminar", cancel: "Cancelar" },
                },
                () => {}
            );
            if (!result || result.type === "error") return;
        }
        set({ files: draft.files.filter((item) => item !== file) });
    };

    const save = async () => {
        const problem = validate(draft);
        if (problem) {
            setSection(problem.section);
            setError(problem.message);
            return;
        }
        setSaving(true);
        try {
            const payload = toPayload(draft);
            delete payload.id;
            const url = draft.id ? `${urlAPI_3}assistant/${draft.id}` : `${urlAPI_3}createAssistant`;
            const response = await fetchWithAuth(url, { method: "POST", mode: "cors", body: JSON.stringify(payload) });
            const result = await response.json();
            if (!response.ok || result.type === "error") {
                sendToast(result?.message ? result : { type: "error", message: "No se pudo guardar el asistente." });
                return;
            }
            sendToast(result);
            onSaved();
        } catch (saveError) {
            sendToast({ type: "error", message: "No se pudo guardar el asistente. Intenta nuevamente." });
        } finally {
            setSaving(false);
        }
    };

    const back = async () => {
        if (dirty) {
            const leave = await questionToast(
                {
                    isFetch: false,
                    title: "Tienes cambios sin guardar",
                    text: "Si sales ahora se pierden.",
                    icon: "warning",
                    buttons: { success: "Salir", cancel: "Seguir editando" },
                },
                () => {}
            );
            if (!leave) return;
        }
        onBack();
    };

    const runTest = async (input, bubble) => {
        if (chatBusy) return;
        setChat((prev) => [...prev, { from: "user", text: bubble, time: nowTime() }]);
        setChatBusy(true);
        try {
            const response = await fetchWithAuth(`${urlAPI_3}assistantTest`, {
                method: "POST",
                mode: "cors",
                body: JSON.stringify({ assistant: toPayload(draft), ...input, previousResponseId }),
            });
            const result = await response.json();
            if (!response.ok || result.type === "error") {
                setChat((prev) => [...prev, { from: "error", text: result?.message || "No se pudo probar el asistente." }]);
                return;
            }
            const data = result.data || {};
            const heard = data.transcription ? [{ from: "note", text: `Entendió: «${data.transcription}»` }] : [];
            const tools = (data.traces || []).map((trace) => ({ from: "tool", text: `${trace.name}${trace.simulated ? " · simulado" : ""}` }));
            setPreviousResponseId(data.responseId || null);
            setChat((prev) => [...prev, ...heard, ...tools, { from: "bot", text: data.text, sources: data.sources || [], time: nowTime() }]);
        } catch (chatError) {
            setChat((prev) => [...prev, { from: "error", text: "No se pudo conectar para probar el asistente." }]);
        } finally {
            setChatBusy(false);
        }
    };

    const sendChat = () => {
        const message = chatText.trim();
        if (!message) return;
        setChatText("");
        runTest({ message }, message);
    };

    const sendAudio = (blob) => {
        if (!blob?.size) return;
        if (blob.size > MAX_TEST_AUDIO_BYTES) {
            setChat((prev) => [...prev, { from: "error", text: "El audio supera los 3 MB." }]);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            const data = String(reader.result || "").split(",")[1] || "";
            runTest({ audio: { data, mime_type: (blob.type || "audio/webm").split(";")[0] } }, "🎤 Nota de voz");
        };
        reader.readAsDataURL(blob);
    };

    const toggleRecording = async () => {
        if (recording) {
            recorderRef.current?.stop();
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            const chunks = [];
            recorder.ondataavailable = (event) => event.data.size && chunks.push(event.data);
            recorder.onstop = () => {
                stream.getTracks().forEach((track) => track.stop());
                setRecording(false);
                sendAudio(new Blob(chunks, { type: recorder.mimeType || "audio/webm" }));
            };
            recorderRef.current = recorder;
            recorder.start();
            setRecording(true);
        } catch (micError) {
            setChat((prev) => [...prev, { from: "error", text: "No se pudo usar el micrófono. Sube un archivo de audio." }]);
        }
    };

    useEffect(() => () => recorderRef.current?.state === "recording" && recorderRef.current.stop(), []);

    const resetChat = () => {
        setChat([]);
        setPreviousResponseId(null);
    };

    const inputStyle = {
        width: "100%",
        boxSizing: "border-box",
        padding: "10px 12px",
        borderRadius: "10px",
        backgroundColor: theme.inputBg,
        border: `1px solid ${theme.inputBorder}`,
        color: theme.text,
        fontSize: "14px",
        outline: "none",
    };
    const label = { fontSize: "12.5px", fontWeight: 600, color: theme.soft, margin: 0 };
    const hint = { fontSize: "12px", color: theme.muted, lineHeight: 1.5 };
    const field = { display: "flex", flexDirection: "column", gap: "6px" };
    const heading = (title, text) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <h2 style={{ margin: 0, fontWeight: 600, fontSize: "20px", color: theme.title }}>{title}</h2>
            <span style={{ fontSize: "13px", color: theme.soft }}>{text}</span>
        </div>
    );
    const segmented = (options, value, onChange) => (
        <div style={{ display: "flex", gap: "8px" }}>
            {options.map((option) => {
                const active = value === option.value;
                return (
                    <button
                        key={option.value}
                        type="button"
                        aria-pressed={active}
                        onClick={() => onChange(option.value)}
                        style={{
                            flex: 1,
                            padding: "9px 12px",
                            borderRadius: "9px",
                            border: "none",
                            backgroundColor: active ? theme.chipActive : theme.chip,
                            color: active ? "#ffffff" : theme.soft,
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: "pointer",
                        }}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
    const toolCard = ({ id, icon: Icon, color, title, text, on, onToggle, children }) => (
        <div style={{ borderRadius: "14px", backgroundColor: theme.optionBg, border: `1px solid ${on ? color : theme.optionBorder}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "16px 18px" }}>
                <span style={{ width: "36px", height: "36px", minWidth: "36px", borderRadius: "10px", backgroundColor: color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon style={{ fontSize: "18px" }} />
                </span>
                <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                    <strong style={{ fontSize: "14px", color: theme.text }}>{title}</strong>
                    <span style={{ fontSize: "12px", color: theme.soft }}>{text}</span>
                </span>
                <Toggle id={id} checked={on} onChange={onToggle} />
            </div>
            {on && <div style={{ padding: "4px 18px 18px", display: "flex", flexDirection: "column", gap: "14px", borderTop: `1px solid ${theme.optionBorder}`, paddingTop: "16px" }}>{children}</div>}
        </div>
    );

    const transferOptions = draft.transferType === "area" ? listAreas : listAdvisers;

    return (
        <div ref={rootRef} className="su-kit su-list-root d-flex flex-column" style={{ height: rootHeight ? `${rootHeight}px` : undefined }}>
            <style>{SCROLL_CSS}</style>
            <style>{BOT_LIST_CSS}</style>
            <style>{WA_CSS}</style>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", minHeight: "60px", padding: "8px 14px", borderBottom: `1px solid ${theme.asideBorder}`, backgroundColor: theme.header, flexShrink: 0 }}>
                <button type="button" onClick={back} style={{ display: "inline-flex", alignItems: "center", gap: "2px", padding: "4px 6px", background: "transparent", border: "none", borderRadius: "8px", color: theme.soft, fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                    <LuChevronLeft className="su-ic" size={16} />
                    Asistentes
                </button>
                <span style={{ width: "1px", height: "22px", margin: "0 4px", backgroundColor: theme.asideBorder, flexShrink: 0 }} />
                <h1 style={{ margin: 0, minWidth: 0, fontFamily: "inherit", fontWeight: 600, fontSize: "19px", color: theme.title, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {draft.name.trim() || (draft.id ? "Asistente" : "Nuevo asistente")}
                </h1>
                {dirty && <span className="su-tag" style={{ "--c": "#e0a32e" }}>Cambios sin guardar</span>}
                <button type="button" className="su-btn" style={{ marginLeft: "auto" }} onClick={save} disabled={saving || uploading}>
                    {saving ? "Guardando…" : "Guardar"}
                </button>
            </div>

            <div style={{ flex: 1, minHeight: 0, display: "flex", backgroundColor: theme.dialog, overflow: "hidden" }}>
                <div role="navigation" aria-label="Secciones del asistente" style={{ flex: "0 0 210px", padding: "16px 12px", backgroundColor: theme.dialog, borderRight: `1px solid ${theme.asideBorder}`, display: "flex", flexDirection: "column", gap: "4px" }}>
                    {SECTIONS.map((item) => {
                        const active = section === item.key;
                        const Icon = item.icon;
                        const count = item.key === "tools" ? activeTools : item.key === "knowledge" ? knowledgeCount : null;
                        return (
                            <button
                                key={item.key}
                                type="button"
                                onClick={() => setSection(item.key)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    padding: "10px 12px",
                                    borderRadius: "10px",
                                    border: "none",
                                    backgroundColor: active ? theme.navOn : "transparent",
                                    color: active ? theme.navOnText : theme.soft,
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    textAlign: "left",
                                    cursor: "pointer",
                                }}
                            >
                                <span style={{ width: "28px", height: "28px", flexShrink: 0, display: "grid", placeItems: "center", borderRadius: "8px", backgroundColor: item.color, color: "#ffffff" }}>
                                    <Icon style={{ fontSize: "15px" }} />
                                </span>
                                {item.label}
                                {count ? <span style={{ marginLeft: "auto", padding: "1px 8px", borderRadius: "99px", backgroundColor: theme.navOn, color: theme.navOnText, fontSize: "12px", fontWeight: 600 }}>{count}</span> : null}
                            </button>
                        );
                    })}
                </div>

                <div className={SCROLL_CLASS} style={{ flex: "1 1 480px", minWidth: 0, overflowY: "auto", padding: "22px 26px", display: "flex", flexDirection: "column", gap: "20px", backgroundColor: theme.aside }}>
                    {error && (
                        <div role="alert" style={{ display: "flex", gap: "10px", alignItems: "center", padding: "10px 14px", borderRadius: "8px", border: "1px solid #FF2242", backgroundColor: theme.dialog, color: theme.text, fontSize: "13px" }}>
                            <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#FF2242", flexShrink: 0 }} />
                            {error}
                        </div>
                    )}

                    {section === "personality" && (
                        <>
                            {heading("Personalidad", "Cómo se presenta, cómo habla y qué debe lograr.")}
                            <div style={field}>
                                <label htmlFor="as-name" style={label}>Nombre del asistente</label>
                                <input id="as-name" style={inputStyle} value={draft.name} onChange={(event) => set({ name: event.target.value })} placeholder="Asesor de ventas" />
                                <span style={hint}>Solo lo ves tú.</span>
                            </div>
                            <div style={field}>
                                <label htmlFor="as-role" style={label}>Se presenta como</label>
                                <textarea id="as-role" rows={2} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }} value={draft.role} onChange={(event) => set({ role: event.target.value })} />
                                <span style={hint}>Quién es y para qué empresa trabaja.</span>
                            </div>
                            <div style={field}>
                                <label htmlFor="as-goal" style={label}>Qué debe lograr</label>
                                <textarea id="as-goal" rows={6} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.55 }} value={draft.instructions_message} onChange={(event) => set({ instructions_message: event.target.value })} />
                                <span style={hint}>Escríbelo como se lo explicarías a una persona nueva: objetivo, tono y lo que nunca debe hacer.</span>
                            </div>
                        </>
                    )}

                    {section === "knowledge" && (
                        <>
                            {heading("Conocimiento", "Lo que el asistente sabe de tu empresa. Si algo no está aquí, lo dice.")}
                            <div style={field}>
                                <span style={label}>Información de la empresa</span>
                                {segmented(
                                    [
                                        { value: "1", label: "Escribirla" },
                                        { value: "2", label: "Hoja de Google" },
                                    ],
                                    draft.source,
                                    (value) => set({ source: value })
                                )}
                            </div>
                            {draft.source === "1" ? (
                                <div style={field}>
                                    <label htmlFor="as-content" style={label}>Información</label>
                                    <textarea id="as-content" rows={8} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.55 }} value={draft.content} onChange={(event) => set({ content: event.target.value })} placeholder="Horarios, precios, políticas, servicios…" />
                                </div>
                            ) : (
                                <div style={field}>
                                    <label htmlFor="as-url" style={label}>Enlace de la hoja</label>
                                    <input id="as-url" style={inputStyle} value={draft.url} onChange={(event) => set({ url: event.target.value })} placeholder="https://docs.google.com/spreadsheets/d/…" />
                                    <span style={hint}>La hoja debe estar compartida como «cualquier persona con el enlace». Se lee en cada conversación.</span>
                                </div>
                            )}

                            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <span style={label}>Preguntas frecuentes</span>
                                    <span style={hint}>El asistente responde con esto cuando le pregunten algo parecido</span>
                                    <button type="button" className="su-btn ghost sm" style={{ marginLeft: "auto" }} onClick={() => set({ faqs: [...draft.faqs, { question: "", answer: "" }] })} disabled={draft.faqs.length >= 50}>
                                        <LuPlus className="su-ic" size={14} />
                                        Agregar pregunta
                                    </button>
                                </div>
                                {draft.faqs.length === 0 ? (
                                    <span style={{ ...hint, padding: "14px", borderRadius: "8px", border: `1px dashed ${theme.inputBorder}`, textAlign: "center" }}>
                                        Ej.: «¿Tienen domicilio?» → «Sí, en toda la ciudad, de 8 a. m. a 8 p. m.»
                                    </span>
                                ) : (
                                    draft.faqs.map((faq, index) => (
                                        <div key={index} style={{ display: "flex", gap: "10px", alignItems: "flex-start", padding: "12px 14px", borderRadius: "12px", border: `1px solid ${theme.optionBorder}`, backgroundColor: theme.selectedBg }}>
                                            <span style={{ fontWeight: 600, fontSize: "15px", color: theme.muted, minWidth: "20px", paddingTop: "9px" }}>{index + 1}</span>
                                            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "8px" }}>
                                                <input aria-label={`Pregunta ${index + 1}`} style={inputStyle} value={faq.question} maxLength={300} onChange={(event) => setFaq(index, { question: event.target.value })} placeholder="Pregunta del cliente" />
                                                <textarea aria-label={`Respuesta ${index + 1}`} rows={2} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }} value={faq.answer} maxLength={1500} onChange={(event) => setFaq(index, { answer: event.target.value })} placeholder="Respuesta" />
                                            </div>
                                            <button type="button" aria-label={`Quitar pregunta ${index + 1}`} onClick={() => set({ faqs: draft.faqs.filter((_, i) => i !== index) })} style={{ border: "none", background: "transparent", color: theme.muted, cursor: "pointer", padding: "9px 4px", display: "flex" }}>
                                                <LuTrash2 style={{ fontSize: "15px" }} />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <span style={label}>Archivos</span>
                                    <span style={hint}>PDF, TXT, Word o PowerPoint</span>
                                    <button type="button" className="su-btn sm" style={{ marginLeft: "auto" }} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                                        <LuUpload className="su-ic" size={14} />
                                        {uploading ? "Subiendo…" : "Subir archivo"}
                                    </button>
                                    <input ref={fileInputRef} type="file" multiple accept=".pdf,.txt,.doc,.docx,.pptx" style={{ display: "none" }} onChange={onFiles} />
                                </div>
                                {draft.files.length === 0 ? (
                                    <span style={{ ...hint, padding: "14px", borderRadius: "8px", border: `1px dashed ${theme.inputBorder}`, textAlign: "center" }}>Aún no hay archivos.</span>
                                ) : (
                                    draft.files.map((file, index) => (
                                        <div key={`${file.name}-${index}`} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 14px", borderRadius: "12px", border: `1px solid ${theme.optionBorder}`, backgroundColor: theme.selectedBg }}>
                                            <LuFileText style={{ fontSize: "18px", color: theme.muted, flexShrink: 0 }} />
                                            <a href={file.url} target="_blank" rel="noreferrer" style={{ flex: 1, minWidth: 0, fontSize: "13px", color: theme.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {file.name}
                                            </a>
                                            <span style={{ fontSize: "11px", fontWeight: 600, color: file.id ? "#2fae4e" : theme.muted, whiteSpace: "nowrap" }}>{file.id ? "Listo" : "Se procesa al guardar"}</span>
                                            <button type="button" aria-label={`Quitar ${file.name}`} onClick={() => removeFile(file)} style={{ border: "none", background: "transparent", color: theme.muted, cursor: "pointer", padding: "4px", display: "flex" }}>
                                                <LuTrash2 style={{ fontSize: "15px" }} />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}

                    {section === "tools" && (
                        <>
                            {heading("Herramientas", "Lo que el asistente puede hacer además de conversar.")}
                            {toolCard({
                                id: "as-transfer",
                                icon: LuHeadphones,
                                color: "#f56f89",
                                title: "Transferir a un asesor",
                                text: "Cuando el contacto pida hablar con una persona.",
                                on: draft.transferOn,
                                onToggle: (on) => set({ transferOn: on }),
                                children: (
                                    <>
                                        {segmented(
                                            [
                                                { value: "agent", label: "A un asesor" },
                                                { value: "area", label: "A un área" },
                                            ],
                                            draft.transferType,
                                            (value) => set({ transferType: value, transferTarget: null })
                                        )}
                                        <Select
                                            styles={styles}
                                            classNamePrefix="select"
                                            placeholder={draft.transferType === "area" ? "Elige un área" : "Elige un asesor"}
                                            value={draft.transferTarget}
                                            options={transferOptions}
                                            onChange={(option) => set({ transferTarget: option })}
                                            noOptionsMessage={() => "Sin opciones"}
                                            menuPortalTarget={document.body}
                                        />
                                        <div style={field}>
                                            <label htmlFor="as-transfer-msg" style={label}>Mensaje al transferir</label>
                                            <input id="as-transfer-msg" style={inputStyle} value={draft.transferMessage} onChange={(event) => set({ transferMessage: event.target.value })} />
                                        </div>
                                    </>
                                ),
                            })}

                            {toolCard({
                                id: "as-schedule",
                                icon: LuCalendarClock,
                                color: "#448aff",
                                title: "Agendar citas",
                                text: "Agenda, consulta y cancela citas con tu sistema de agenda.",
                                on: draft.scheduleOn,
                                onToggle: (on) => set({ scheduleOn: on }),
                                children: (
                                    <>
                                        <div style={field}>
                                            <label htmlFor="as-sch-create" style={label}>Webhook para agendar</label>
                                            <input id="as-sch-create" style={inputStyle} value={draft.scheduleWebhook} onChange={(event) => set({ scheduleWebhook: event.target.value })} placeholder="https://" />
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" }}>
                                            <div style={field}>
                                                <label htmlFor="as-sch-search" style={label}>Webhook para buscar citas</label>
                                                <input id="as-sch-search" style={inputStyle} value={draft.scheduleSearch} onChange={(event) => set({ scheduleSearch: event.target.value })} placeholder="https://" />
                                            </div>
                                            <div style={field}>
                                                <label htmlFor="as-sch-cancel" style={label}>Webhook para cancelar</label>
                                                <input id="as-sch-cancel" style={inputStyle} value={draft.scheduleCancel} onChange={(event) => set({ scheduleCancel: event.target.value })} placeholder="https://" />
                                            </div>
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" }}>
                                            <div style={field}>
                                                <span style={label}>Plantilla del recordatorio</span>
                                                <Select
                                                    styles={styles}
                                                    classNamePrefix="select"
                                                    placeholder="Elige una plantilla"
                                                    value={draft.reminderTemplate}
                                                    options={reminderTemplates}
                                                    getOptionLabel={(option) => option.name}
                                                    getOptionValue={(option) => option.id || option._id || option.name}
                                                    onChange={(option) => set({ reminderTemplate: option })}
                                                    noOptionsMessage={() => "Ninguna plantilla aprobada tiene 4 parámetros"}
                                                    menuPortalTarget={document.body}
                                                />
                                            </div>
                                            <div style={field}>
                                                <span style={label}>Se envía</span>
                                                <Select
                                                    styles={styles}
                                                    classNamePrefix="select"
                                                    placeholder="Elige cuándo"
                                                    value={draft.reminderTime}
                                                    options={REMINDER_HOURS}
                                                    onChange={(option) => set({ reminderTime: option })}
                                                    menuPortalTarget={document.body}
                                                />
                                            </div>
                                        </div>
                                        <span style={hint}>La plantilla necesita 4 parámetros: nombre, fecha, hora y motivo de la cita.</span>
                                    </>
                                ),
                            })}

                            {toolCard({
                                id: "as-sales",
                                icon: LuShoppingCart,
                                color: "#00a884",
                                title: "Registrar ventas",
                                text: "Cuando el contacto muestre interés en comprar, envía sus datos a tu sistema.",
                                on: draft.salesOn,
                                onToggle: (on) => set({ salesOn: on }),
                                children: (
                                    <div style={field}>
                                        <label htmlFor="as-sales-hook" style={label}>Webhook de ventas</label>
                                        <input id="as-sales-hook" style={inputStyle} value={draft.salesWebhook} onChange={(event) => set({ salesWebhook: event.target.value })} placeholder="https://" />
                                        <span style={hint}>Recibe nombre, producto y teléfono del contacto.</span>
                                    </div>
                                ),
                            })}

                            <span style={{ fontWeight: 600, fontSize: "16px", color: theme.muted, marginTop: "6px" }}>CRM y contacto</span>
                            {CRM_TOOLS.map((tool) => {
                                const config = draft.crm[tool.key];
                                const options = crmChoices[tool.itemsKey];
                                const selected = config[tool.itemsKey]
                                    .map((item) => options.find((option) => (option._id ? option._id === item._id : option.name === item.name)) || null)
                                    .filter(Boolean);
                                return (
                                    <React.Fragment key={tool.key}>
                                        {toolCard({
                                            id: `as-crm-${tool.key}`,
                                            icon: tool.icon,
                                            color: tool.color,
                                            title: tool.title,
                                            text: tool.text,
                                            on: config.enabled,
                                            onToggle: (on) => setCrm(tool.key, { enabled: on }),
                                            children: (
                                                <>
                                                    <div style={field}>
                                                        <span style={label}>{tool.pick}</span>
                                                        <Select
                                                            isMulti
                                                            styles={{
                                                                ...styles,
                                                                multiValue: (provided) => ({ ...provided, backgroundColor: theme.chip, borderRadius: "12px" }),
                                                                multiValueLabel: (provided) => ({ ...provided, color: theme.text, fontSize: "12px" }),
                                                                multiValueRemove: (provided) => ({ ...provided, color: theme.muted, borderRadius: "12px" }),
                                                            }}
                                                            classNamePrefix="select"
                                                            placeholder="Elige…"
                                                            value={selected}
                                                            options={options}
                                                            getOptionLabel={(option) => option[tool.optionLabel]}
                                                            getOptionValue={(option) => option._id || option.name}
                                                            onChange={(values) =>
                                                                setCrm(tool.key, {
                                                                    [tool.itemsKey]: (values || []).map((option) =>
                                                                        tool.key === "fields"
                                                                            ? { name: option.name, description: option.description || "" }
                                                                            : { _id: option._id, [tool.optionLabel]: option[tool.optionLabel] }
                                                                    ),
                                                                })
                                                            }
                                                            noOptionsMessage={() => tool.empty}
                                                            menuPortalTarget={document.body}
                                                        />
                                                    </div>
                                                    <div style={field}>
                                                        <label htmlFor={`as-crm-${tool.key}-when`} style={label}>¿Cuándo debe usarla?</label>
                                                        <input
                                                            id={`as-crm-${tool.key}-when`}
                                                            style={inputStyle}
                                                            value={config.whenToUse}
                                                            placeholder={tool.when}
                                                            onChange={(event) => setCrm(tool.key, { whenToUse: event.target.value })}
                                                        />
                                                        <span style={hint}>Si lo dejas vacío usa: «{tool.when}».</span>
                                                    </div>
                                                </>
                                            ),
                                        })}
                                    </React.Fragment>
                                );
                            })}

                            <span style={{ fontWeight: 600, fontSize: "16px", color: theme.muted, marginTop: "6px" }}>Pagos y catálogo</span>
                            {toolCard({
                                id: "as-payment",
                                icon: LuCreditCard,
                                color: "#5bc0de",
                                title: "Link de pago",
                                text: "Genera un cobro con ePayco o Wompi y se lo envía al contacto.",
                                on: draft.crm.payment.enabled,
                                onToggle: (on) => setCrm("payment", { enabled: on }),
                                children: (
                                    <>
                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" }}>
                                            <div style={field}>
                                                <span style={label}>Pasarela</span>
                                                <Select
                                                    styles={styles}
                                                    classNamePrefix="select"
                                                    placeholder="Elige"
                                                    value={PLATFORMS.find((option) => option.value === draft.crm.payment.platform) || null}
                                                    options={PLATFORMS}
                                                    onChange={(option) => setCrm("payment", { platform: option.value })}
                                                    menuPortalTarget={document.body}
                                                />
                                            </div>
                                            <div style={field}>
                                                <span style={label}>Monto</span>
                                                {segmented(
                                                    [
                                                        { value: "fixed", label: "Fijo" },
                                                        { value: "ai", label: "Lo calcula la IA" },
                                                    ],
                                                    draft.crm.payment.amountMode,
                                                    (value) => setCrm("payment", { amountMode: value })
                                                )}
                                            </div>
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" }}>
                                            <div style={field}>
                                                <label htmlFor="as-pay-amount" style={label}>{draft.crm.payment.amountMode === "ai" ? "Monto máximo (COP)" : "Monto (COP)"}</label>
                                                <input
                                                    id="as-pay-amount"
                                                    style={inputStyle}
                                                    inputMode="numeric"
                                                    value={draft.crm.payment.amountMode === "ai" ? draft.crm.payment.maxAmount : draft.crm.payment.amount}
                                                    onChange={(event) => setCrm("payment", { [draft.crm.payment.amountMode === "ai" ? "maxAmount" : "amount"]: event.target.value.replace(/[^\d]/g, "") })}
                                                    placeholder="150000"
                                                />
                                            </div>
                                            <div style={field}>
                                                <label htmlFor="as-pay-title" style={label}>Concepto</label>
                                                <input id="as-pay-title" style={inputStyle} value={draft.crm.payment.title} onChange={(event) => setCrm("payment", { title: event.target.value })} placeholder="Vacío = lo escribe la IA" />
                                            </div>
                                        </div>
                                        <div style={field}>
                                            <label htmlFor="as-pay-when" style={label}>¿Cuándo debe usarla?</label>
                                            <input id="as-pay-when" style={inputStyle} value={draft.crm.payment.whenToUse} onChange={(event) => setCrm("payment", { whenToUse: event.target.value })} placeholder="Cuando el contacto confirme que quiere pagar" />
                                        </div>
                                        <span style={hint}>Usa las llaves configuradas en Métodos de pago de la cuenta.</span>
                                    </>
                                ),
                            })}
                            {toolCard({
                                id: "as-catalog",
                                icon: LuLayoutGrid,
                                color: "#448aff",
                                title: "Enviar catálogo",
                                text: "Envía el botón para ver el catálogo de WhatsApp de la cuenta.",
                                on: draft.crm.catalog.enabled,
                                onToggle: (on) => setCrm("catalog", { enabled: on }),
                                children: (
                                    <>
                                        <div style={field}>
                                            <label htmlFor="as-cat-msg" style={label}>Texto que acompaña el botón</label>
                                            <input id="as-cat-msg" style={inputStyle} value={draft.crm.catalog.message} onChange={(event) => setCrm("catalog", { message: event.target.value })} />
                                        </div>
                                        <div style={field}>
                                            <label htmlFor="as-cat-when" style={label}>¿Cuándo debe usarla?</label>
                                            <input id="as-cat-when" style={inputStyle} value={draft.crm.catalog.whenToUse} onChange={(event) => setCrm("catalog", { whenToUse: event.target.value })} placeholder="Cuando el contacto pregunte por los productos" />
                                        </div>
                                        <span style={hint}>Requiere que la cuenta tenga un catálogo de WhatsApp conectado.</span>
                                    </>
                                ),
                            })}
                        </>
                    )}

                    {section === "settings" && (
                        <>
                            {heading("Ajustes", "Estado, notas de voz, idioma y modelo de IA del asistente.")}
                            <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 16px", borderRadius: "12px", border: `1px solid ${theme.optionBorder}`, backgroundColor: theme.selectedBg }}>
                                <span style={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
                                    <span style={label}>Asistente activo</span>
                                    <span style={hint}>Apagado no responde: los bots que lo usan avisan que no hay asistente disponible.</span>
                                </span>
                                <Toggle id="as-active" checked={draft.active} onChange={(active) => set({ active })} />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "14px 16px", borderRadius: "12px", border: `1px solid ${theme.optionBorder}`, backgroundColor: theme.selectedBg }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                                    <span style={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
                                        <span style={label}>Entender notas de voz</span>
                                        <span style={hint}>Transcribe los audios del cliente y responde como si los hubiera escrito.</span>
                                    </span>
                                    <Toggle id="as-audio" checked={draft.audioOn} onChange={(audioOn) => set({ audioOn })} />
                                </div>
                                {draft.audioOn && (
                                    <div style={{ ...field, maxWidth: "260px" }}>
                                        <span style={label}>Duración máxima</span>
                                        <Select styles={styles} classNamePrefix="select" value={AUDIO_LIMITS.find((option) => option.value === draft.audioMax)} options={AUDIO_LIMITS} onChange={(option) => set({ audioMax: option.value })} menuPortalTarget={document.body} />
                                        <span style={hint}>Los audios más largos se rechazan con amabilidad.</span>
                                    </div>
                                )}
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" }}>
                                <div style={field}>
                                    <span style={label}>Idioma</span>
                                    <Select styles={styles} classNamePrefix="select" value={LANGUAGES.find((option) => option.value === draft.language)} options={LANGUAGES} onChange={(option) => set({ language: option.value })} menuPortalTarget={document.body} />
                                </div>
                                <div style={field}>
                                    <span style={label}>Modelo</span>
                                    <Select
                                        styles={styles}
                                        classNamePrefix="select"
                                        value={MODELS.find((option) => option.value === draft.model) || { value: draft.model, label: draft.model }}
                                        options={MODELS}
                                        onChange={(option) => set({ model: option.value })}
                                        menuPortalTarget={document.body}
                                    />
                                </div>
                            </div>
                            <span style={hint}>Los modelos más grandes razonan mejor pero responden más lento y cuestan más.</span>
                        </>
                    )}
                </div>

                <aside aria-label="Probar en vivo" style={{ flex: "0 1 380px", minWidth: "320px", display: "flex", flexDirection: "column", borderLeft: `1px solid ${theme.asideBorder}` }}>
                    <div className="su-wa">
                        <div className="su-wa-h">
                            <span className="su-wa-av" style={{ background: "#7563D8" }}>
                                <LuSparkles className="su-ic" size={20} />
                            </span>
                            <div className="su-wa-id">
                                <b title={draft.name}>{draft.name.trim() || "Asistente"}</b>
                                <span className={chatBusy ? "typing" : ""}>{chatBusy ? "escribiendo…" : "Conversación de prueba"}</span>
                            </div>
                            <button type="button" className="su-wa-ib" title="Reiniciar conversación" aria-label="Reiniciar conversación" onClick={resetChat}>
                                <LuRotateCcw className="su-ic" size={17} />
                            </button>
                        </div>
                        <div className={`su-wa-msgs ${SCROLL_CLASS}`}>
                            <div className="su-wa-notice">Prueba con el borrador, aunque no esté guardado. Las herramientas se simulan: no transfiere, no agenda y no registra ventas de verdad.</div>
                            {chat.length === 0 && <div className="su-wa-sys">Escribe como lo haría un cliente.</div>}
                            {chat.map((item, index) => {
                                if (item.from === "tool") {
                                    return (
                                        <div key={index} className="su-wa-sys">
                                            <LuWrench className="su-ic" size={13} />
                                            <span>{item.text}</span>
                                        </div>
                                    );
                                }
                                if (item.from === "note") return <div key={index} className="su-wa-heard">{item.text}</div>;
                                if (item.from === "error") return <div key={index} className="su-wa-sys bad" style={{ whiteSpace: "normal" }}>{item.text}</div>;
                                const mine = item.from === "user";
                                return (
                                    <div key={index} className={`su-wa-row${mine ? " me" : ""}`}>
                                        <div className="su-wa-b">
                                            {formatWhatsApp(item.text)}
                                            {item.sources?.length > 0 && (
                                                <div className="su-wa-meta" style={{ justifyContent: "flex-start", marginTop: "4px" }}>
                                                    <LuFileText className="su-ic" size={12} />
                                                    <span>Fuente: {item.sources.join(", ")}</span>
                                                </div>
                                            )}
                                            <div className="su-wa-meta">
                                                <span>{item.time}</span>
                                                {mine && <LuCheckCheck className="su-ic read" size={15} />}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            {chatBusy && (
                                <div className="su-wa-typing" aria-label="El asistente está escribiendo">
                                    <i />
                                    <i />
                                    <i />
                                </div>
                            )}
                            <div ref={chatEndRef} />
                        </div>
                        <div className="su-wa-c">
                            <div className="su-wa-pill">
                                <input
                                    id="assistant-test-message"
                                    aria-label="Mensaje de prueba"
                                    value={chatText}
                                    disabled={chatBusy || recording}
                                    onChange={(event) => setChatText(event.target.value)}
                                    onKeyDown={(event) => event.key === "Enter" && sendChat()}
                                    placeholder={recording ? "Grabando… toca el botón para enviar" : "Escribe un mensaje"}
                                />
                                {draft.audioOn && (
                                    <>
                                        <input
                                            ref={audioInputRef}
                                            type="file"
                                            accept="audio/*"
                                            style={{ display: "none" }}
                                            onChange={(event) => {
                                                const file = event.target.files?.[0];
                                                event.target.value = "";
                                                sendAudio(file);
                                            }}
                                        />
                                        <button type="button" className="su-wa-ib" aria-label="Subir audio" title="Subir audio" onClick={() => audioInputRef.current?.click()} disabled={chatBusy || recording}>
                                            <LuPaperclip className="su-ic" size={18} />
                                        </button>
                                    </>
                                )}
                            </div>
                            {draft.audioOn && !chatText.trim() ? (
                                <button
                                    type="button"
                                    className={`su-wa-round${recording ? " rec" : ""}`}
                                    aria-label={recording ? "Detener y enviar" : "Grabar nota de voz"}
                                    title={recording ? "Detener y enviar" : "Grabar nota de voz"}
                                    aria-pressed={recording}
                                    onClick={toggleRecording}
                                    disabled={chatBusy}
                                >
                                    {recording ? <LuSquare className="su-ic" size={15} /> : <LuMic className="su-ic" size={19} />}
                                </button>
                            ) : (
                                <button type="button" className="su-wa-round" aria-label="Enviar" title="Enviar" onClick={sendChat} disabled={chatBusy || recording || !chatText.trim()}>
                                    <LuSend className="su-ic" size={18} />
                                </button>
                            )}
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default AssistantEditor;
